import { action, computed, makeObservable } from 'mobx';
import { IkeToken } from '@ikegps/core-api';
import { type CollectionModel, CollectionModelFieldLine, ModelMutatorLine } from '@ikegps/core-shared';
import { LineDrawState } from './line.draw.state';
import type { MeasurementPoint } from './measurement.state';
import { Services } from '../services';
import { openLineTypeModal } from '../modules/line.type.modal/line.type.modal';

/** Surface-specific callbacks the active map provides when it enters line-draw mode. */
export interface LineDrawHost {
	standDownOtherTools?(): void;
	/** Re-render the surface's overlay once a line is committed. */
	onComplete?(): void;
}

/** Minimal page-state surface the controller needs — avoids a page.state ↔ controller import cycle. */
interface LineDrawPageRef {
	CollectionLink: { cancelLinking(): void };
}

/**
 * App-global line-draw mode, mirroring CollectionLink: one active draw at a time across every map
 * surface. Owns the in-progress LineDrawState plus the surface-agnostic begin/complete/cancel flow,
 * so navigation/linking/save guards can read a single source of truth instead of a per-page state.
 */
export class LineDrawController {
	readonly state = new LineDrawState();
	private host: LineDrawHost | null = null;

	constructor(private pageState: LineDrawPageRef) {
		makeObservable(this);
	}

	@computed get isActive(): boolean {
		return this.state.isActive;
	}

	@action.bound
	begin(field: CollectionModelFieldLine, lineType: string, host?: LineDrawHost) {
		// Backstop token check; entry points are gated too, but this controller is the single source of truth.
		if (!Services.Token.has(IkeToken.FEATURE_LINE_TOOL)) {
			return;
		}
		// Ignore re-entry while a draw is in progress so in-progress vertices aren't silently discarded.
		if (this.state.isActive) {
			return;
		}
		this.host = host ?? null;
		this.host?.standDownOtherTools?.();
		// Drawing and linking are mutually exclusive; linking is global, so cancel it explicitly.
		this.pageState.CollectionLink.cancelLinking();
		this.state.begin(field, lineType);
		this.setCursor('crosshair');
	}

	@action.bound
	addPoint(point: MeasurementPoint) {
		if (!this.state.isActive) {
			return;
		}
		this.state.addPoint(point);
	}

	@action.bound
	cancel() {
		this.state.reset();
		this.setCursor('');
		this.host = null;
	}

	/** Cancel only if the active draw targets a field on the given collection (per-pole discard in the topo editor). */
	@action.bound
	cancelForCollection(collection: CollectionModel) {
		if (this.state.targetField?.collection === collection) {
			this.cancel();
		}
	}

	complete(): void {
		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		this.runComplete();
	}

	private async runComplete(): Promise<void> {
		const field = this.state.targetField;
		const points = this.state.points;
		if (!field || points.length < 2) {
			return;
		}

		// The type is picked up-front and held as pending; the field is only mutated here, on Complete.
		// Defensive fallback: if pending is somehow empty, prompt rather than commit a typeless line.
		let lineType = this.state.pendingLineType;
		if (!lineType) {
			const chosen = await openLineTypeModal(field.schema, field.lineType);
			if (chosen == null) {
				return;
			}
			lineType = chosen;
		}

		field.Mutator.setLineType(lineType);
		field.Mutator.drawOffice(points.map((p) => ModelMutatorLine.officePoint(p.location.lat(), p.location.lng())));
		const host = this.host;
		this.cancel();
		host?.onComplete?.();
	}

	private setCursor(cursor: string) {
		// The map is a shared singleton; only set the cursor once it exists.
		if (Services.Map.isReady) {
			Services.Map.map.setOptions({ draggableCursor: cursor });
		}
	}
}
